import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { format, Locale } from 'date-fns';
import { useMediaQuery } from '@mui/material';
import { Payment } from '../../../models/PaymentHistory';
import { IMultiAccount } from '../../../models/UserInfo';
import { ServiceTags } from '../../../models/Accounts';
import { PaymentHistoryDetails } from '../PaymentDetails';
import DateVO from '../../../core/valueObjects/DateVO';
import { DateVOv2 } from '../../../core/DateVOv2';

interface MobileCellProps {
  element: Payment;
  locale: Locale | undefined;
  accountInfo: IMultiAccount | undefined | null;
  subAccount: number | undefined;
}

export const MobileCell: FC<MobileCellProps> = ({ element, accountInfo, subAccount, locale }) => {
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language === 'ua' ? 'uk' : i18n.language;
  const dateFormat = currentLocale === 'en' ? 'MMMM dd, yyyy' : 'dd MMMM yyyy';

  const monthOrDailyService = element?.Service?.Daily ? t('services.uahPerDay') : t('services.uahPerMonth');
  /* parsedFromDate, parsedToDate - Необходимо для правильного парсинга даты на платформах с iOs 15 */
  const parsedFromDate = DateVO.create(element.FromDate).get(true);
  const fromDate = element.FromDate && format(new Date(parsedFromDate), dateFormat, { locale });
  const parsedToDate = DateVO.create(element.ToDate).get(true);
  const toDate = element.ToDate && format(new Date(parsedToDate), dateFormat, { locale });
  const isMobile = useMediaQuery('(max-width:767px)');

  const getMappedTitlesBy = (amount: number, typeId: number) => {
    const mappedTitles = {
      1: amount > 0 ? t('paymentHistory.balanceReplenishment') : t('servicePayment.backAmountBy'),
      2: t('servicePayment.serviceActivationBy'),
      3: t('paymentHistory.balanceReplenishment'),
      4: t('servicePayment.backAmountBy'),
    };
    return mappedTitles?.[typeId];
  };

  const getMappedTitlesFor = (userID: number, amount: number, typeId: number) => {
    const mappedTitles = {
      1: amount > 0 ? t('paymentHistory.balanceReplenishment') : t('servicePayment.backAmountFor', { userID }),
      2: t('servicePayment.serviceActivationFor', { userID }),
      3: t('paymentHistory.balanceReplenishment'),
      4: t('servicePayment.backAmountFor', { userID }),
    };
    return mappedTitles?.[typeId];
  };

  const getTitleFromTypeIdWithRole = (typeId: number, userID: number, byUserID: number, amount: number) => {
    const defaultMappedTitles = {
      1: amount > 0 ? t('paymentHistory.balanceReplenishment') : t('servicePayment.backAmount'),
      2: t('servicePayment.serviceActivation'),
      3: t('paymentHistory.balanceReplenishment'),
      4: t('servicePayment.backAmount'),
    };
    switch (accountInfo?.Role) {
      case 'admin':
        if (subAccount === accountInfo.UserID && subAccount !== userID) {
          return getMappedTitlesFor(userID, amount, typeId);
        }
        if (subAccount !== accountInfo.UserID && subAccount !== byUserID) {
          return getMappedTitlesBy(amount, typeId);
        }
        return defaultMappedTitles?.[typeId];
      case 'member':
        if (accountInfo.UserID !== byUserID) {
          return getMappedTitlesBy(amount, typeId);
        }
        return defaultMappedTitles?.[typeId];
      default:
        return defaultMappedTitles?.[typeId];
    }
  };

  const getDefaultTitleFromTypeId = (typeId: number, amount: number) => {
    const mappedTitles = {
      1: amount > 0 ? t('paymentHistory.balanceReplenishment') : t('servicePayment.backAmount'),
      2: t('servicePayment.serviceActivation'),
      3: t('paymentHistory.balanceReplenishment'),
      4: t('servicePayment.backAmount'),
    };
    return mappedTitles?.[typeId];
  };

  const getClassBorder = (typeId: number, amount: number) => {
    const mappedTitles = {
      1: `mobile-cell__status ${amount > 0 ? 'top-up' : 'refund'}`,
      2: 'mobile-cell__status pay',
      3: 'mobile-cell__status top-up',
      4: 'mobile-cell__status refund',
    };
    return mappedTitles?.[typeId];
  };

  return (
    <div className="mobile-cell">
      <div className={getClassBorder(element.TypeID, element.Amount) || `mobile-cell__status`} />
      <div className="mobile-cell__container">
        <div className="mobile-cell__container__title">
          {element.TypeID === 2 &&
          element?.Service?.Name &&
          element?.Service?.Tags?.some((tag) => tag.id === ServiceTags.Suspension) &&
          element.ServicesEx &&
          element.ServicesEx.length === 0 ? (
            <p>{element?.Service?.Name}</p>
          ) : (
            <p>
              {!element.ByUserID || element.UserID === element.ByUserID
                ? getDefaultTitleFromTypeId(element.TypeID, element.Amount)
                : getTitleFromTypeIdWithRole(element.TypeID, element.UserID, element.ByUserID, element.Amount)}
            </p>
          )}
          {isMobile ? (
            <div className="tooltip">
              <p>
                {Math.abs(element.Amount)} {t('common.uah')}
              </p>
            </div>
          ) : null}
        </div>
        {element?.PaymentSystem && (
          <div className="mobile-cell__container__replenishment">
            <p>{element.PaymentSystem?.name}</p>
            {element.PaymentSystem?.method && <p>{`: ${element.PaymentSystem?.method}`}</p>}
          </div>
        )}
        {(element.TypeID === 2 || element.TypeID === 4) &&
        (element?.Service?.Name || (element.ServicesEx && element.ServicesEx.length > 0) || (fromDate && toDate)) ? (
          <div className="mobile-cell__container__details">
            <div className="mobile-cell__container__details__services">
              {element?.Service?.Name &&
              (!element?.Service?.Tags?.some((tag) => tag.id === ServiceTags.Suspension) ||
                (element?.Service?.Tags?.some((tag) => tag.id === ServiceTags.Suspension) &&
                  (element.TypeID === 4 ||
                    (element.TypeID === 2 && element.ServicesEx && element.ServicesEx.length > 0)))) ? (
                <span className="service-name">{element?.Service?.Name}</span>
              ) : null}
              {element.ServicesEx &&
                element.ServicesEx.length > 0 &&
                element.ServicesEx.map((el) => (
                  <span className="service-ex-name" key={el.id}>
                    + {el.Name} {`(${el.Price} ${el.IsOnce ? t('common.uah') : monthOrDailyService})`}
                  </span>
                ))}
              {fromDate && toDate ? (
                <span className="service-period">
                  {t('paymentHistory.paymentPeriod', {
                    from: fromDate,
                    to: toDate,
                  })}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
        {isMobile ? (
          <>
            <div className="mobile-cell__container__pay-details">
              <PaymentHistoryDetails element={element} />
            </div>
            <div className="mobile-cell__container__pay-date">
              <span className="title">{t('paymentHistory.date')}:&nbsp;</span>
              <span>
                {new DateVOv2({ format: 'yyyy-MM-dd HH:mm:ss', dateString: element?.PayDate }).getDateString(
                  currentLocale,
                )}
              </span>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};
